import React, { useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { recruitingAPI } from "../services/recruitingApi";
import JobForm from "../components/JobForm";
import ApplicationForm from "../components/ApplicationForm";
import JobDescriptionExtension from "../components/JobDescriptionExtension";
import JobCandidateFinder from "../components/JobCandidateFinder";
import PublicProfileSearch from "../components/PublicProfileSearch";
import LinkedInPostGenerator from "../components/LinkedInPostGenerator";
import EmailComposer from "../components/EmailComposer";
import JobSpecificCVGeneration from "../components/JobSpecificCVGeneration";
import JobStatusControl from "../components/JobStatusControl";
import JobDetailSidebar from "../components/JobDetailSidebar";
import SourcingTab from "../components/SourcingTab";
import StatusDistributionBar from "../components/StatusDistributionBar";
import ApplicationStatusTimeline from "../components/ApplicationStatusTimeline";
import QuickStatusChange from "../components/QuickStatusChange";
import ApplicationListView from "../components/ApplicationListView";
import useInlineEdit, { required } from "../hooks/useInlineEdit";
import InlineEditableField from "../components/InlineEditableField";
import {
  APPLICATION_STATUS_CONFIG,
  APPLICATION_STATUS_ORDER,
} from "../components/StatusControl/configs/applicationConfig";
import { Chip, ToggleButtonGroup, ToggleButton } from "@mui/material";
import { List, LayoutGrid } from "lucide-react";
import { JobActivitySummaryResponse } from "../constants/jobActivityConfig";
import { getCurrentUser } from "../utils/auth";
import { safeStringToArray } from "../utils/dataHelpers";
import "./JobDetail.css";

interface CurrentUser {
  id: number;
  role: string;
}

interface AssignedRecruiter {
  recruiter_id: number;
  recruiter_name?: string;
  username?: string;
}

interface Job {
  id: number;
  title: string;
  department?: string;
  location?: string;
  employment_type?: string;
  seniority?: string;
  status?: string;
  status_changed_at?: string;
  owner_user_id?: number;
  owner_name?: string;
  customer_id?: number;
  customer_name?: string;
  customer_company_name?: string;
  customer_rate?: number;
  engineer_rate?: number;
  description?: string;
  skills?: string[] | string;
  tags?: string[] | string;
  assigned_recruiters?: AssignedRecruiter[];
  created_at?: string;
}

interface Application {
  id: number;
  candidate_id: number;
  candidate_first_name?: string;
  candidate_last_name?: string;
  first_name?: string;
  last_name?: string;
  candidate_email?: string;
  email?: string;
  main_skill?: string;
  seniority?: string;
  status: string;
  ai_match_score?: number;
  status_changed_at?: string;
  expected_rate?: number;
}

interface Contact {
  name?: string;
  role?: string;
  email?: string;
  phone?: string;
}

interface Company {
  id: number;
  name?: string;
  company_name?: string;
  industry?: string;
  company_size?: string;
  employee_count?: number;
  founded_year?: number;
  company_type?: string;
  tax_id?: string;
  email?: string;
  phone?: string;
  website?: string;
  address?: string;
  status?: string;
  description?: string;
  notes?: string;
  additional_contacts?: Contact[];
}

interface Recruiter {
  id: number;
  username: string;
  email: string;
  role: string;
}

interface JobResponse {
  success: boolean;
  data?: Job;
}

interface ApplicationsResponse {
  success: boolean;
  data?: Application[];
}

interface CompanyResponse {
  success: boolean;
  data?: Company;
}

interface NotesResponse {
  success: boolean;
  data?: Array<{
    id: number;
    content: string;
    author_name?: string;
    created_at: string;
  }>;
}

interface AdminUsersResponse {
  users?: Recruiter[];
}

type TabType = "overview" | "applications" | "sourcing" | "ai-tools";

const VALID_TABS = new Set<TabType>([
  "overview",
  "applications",
  "sourcing",
  "ai-tools",
]);

const EDITABLE_JOB_FIELDS: (keyof Job)[] = [
  "title",
  "department",
  "location",
  "employment_type",
  "seniority",
  "description",
  "customer_rate",
  "engineer_rate",
];

const JobDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation(["recruiting", "common"]);
  const queryClient = useQueryClient();
  const initialTab = searchParams.get("tab") as TabType;
  const [activeTab, setActiveTab] = useState<TabType>(
    VALID_TABS.has(initialTab) ? initialTab : "overview",
  );
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateApplicationModal, setShowCreateApplicationModal] =
    useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedCandidateForEmail, setSelectedCandidateForEmail] = useState<
    number | null
  >(null);
  const [showCVModal, setShowCVModal] = useState(false);
  const [selectedApplicationForCV, setSelectedApplicationForCV] =
    useState<Application | null>(null);
  const [selectedRecruiterId, setSelectedRecruiterId] = useState("");
  const [isAssigningRecruiter, setIsAssigningRecruiter] = useState(false);
  const [removingRecruiterId, setRemovingRecruiterId] = useState<number | null>(
    null,
  );
  const [appStatusFilter, setAppStatusFilter] = useState<string | null>(null);
  const [deleteAppId, setDeleteAppId] = useState<number | null>(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteNote, setDeleteNote] = useState("");
  const [appViewMode, setAppViewMode] = useState<"rows" | "cards">("rows");
  const [descExpanded, setDescExpanded] = useState(false);
  const currentUser = (getCurrentUser() || {}) as CurrentUser;

  // ── Queries ──────────────────────────────────────────────────
  const {
    data: jobData,
    isLoading: jobLoading,
    error: jobError,
  } = useQuery<JobResponse>(
    ["job", id],
    () =>
      recruitingAPI.getJob(
        Number.parseInt(id!, 10),
      ) as unknown as Promise<JobResponse>,
    { enabled: !!id },
  );

  const { data: applicationsData, isLoading: applicationsLoading } =
    useQuery<ApplicationsResponse>(
      ["job-applications", id],
      () =>
        recruitingAPI.getApplications({
          job_id: id,
        }) as unknown as Promise<ApplicationsResponse>,
      { enabled: !!id },
    );

  // Fetch job notes (pre-warm cache for sidebar)
  useQuery<NotesResponse>(
    ["job-notes", id],
    () =>
      recruitingAPI.getNotes(
        "job",
        Number.parseInt(id!, 10),
      ) as unknown as Promise<NotesResponse>,
    {
      enabled: !!id,
    },
  );

  // Fetch job activity summary (new comprehensive timeline)
  const { data: activitySummaryData, isLoading: activityLoading } =
    useQuery<JobActivitySummaryResponse>(
      ["job-activity-summary", id],
      () =>
        recruitingAPI.getJobActivitySummary(
          Number.parseInt(id!, 10),
        ) as unknown as Promise<JobActivitySummaryResponse>,
      { enabled: !!id },
    );

  const job = jobData?.data;
  const applications =
    applicationsData?.data ||
    (Array.isArray(applicationsData) ? applicationsData : []);
  const canManageAssignments =
    job &&
    (currentUser.role === "application_admin" ||
      currentUser.role === "organization_admin" ||
      currentUser.id === job.owner_user_id);

  // Company data (for overview tab)
  const { data: companyData } = useQuery<CompanyResponse>(
    ["company", job?.customer_id],
    () =>
      recruitingAPI.getCustomer(
        job?.customer_id ?? 0,
      ) as unknown as Promise<CompanyResponse>,
    { enabled: !!job?.customer_id },
  );

  const { data: companyNotesData } = useQuery<NotesResponse>(
    ["company-notes", job?.customer_id],
    () =>
      recruitingAPI.getNotes(
        "customer",
        job?.customer_id ?? 0,
      ) as unknown as Promise<NotesResponse>,
    { enabled: !!job?.customer_id },
  );

  const company = companyData?.data;
  const companyNotes = companyNotesData?.data || [];

  // ── Inline Edit ──────────────────────────────────────────────
  const {
    isEditMode,
    editedEntity: editedJob,
    isDirty,
    validationErrors,
    isSaving,
    handleStartEdit,
    handleCancelEdit,
    handleFieldChange,
    handleSaveEdit,
  } = useInlineEdit<Job>({
    entity: job || null,
    queryKey: ["job", id],
    saveFn: (data) =>
      recruitingAPI.updateJob(
        Number.parseInt(id!, 10),
        data as Record<string, unknown>,
      ) as Promise<unknown>,
    editableFields: EDITABLE_JOB_FIELDS,
    validationRules: [required<Job>("title", "Title is required")],
  });

  // ── Recruiter fetch ──────────────────────────────────────────
  const { data: recruitersData } = useQuery<AdminUsersResponse>(
    ["recruiters-by-role", "recruiter"],
    () =>
      recruitingAPI.getUsersByRole(
        "recruiter",
      ) as unknown as Promise<AdminUsersResponse>,
    { enabled: !!canManageAssignments, staleTime: 5 * 60 * 1000 },
  );
  const recruiters = recruitersData?.users || [];

  // ── Mutations ────────────────────────────────────────────────
  const deleteApplicationMutation = useMutation(
    async ({
      appId,
      reason,
      note,
    }: {
      appId: number;
      reason: string;
      note: string;
    }) => {
      return await recruitingAPI.deleteApplication(appId, {
        reason: reason || undefined,
        note: note || undefined,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["job-applications", id]);
        setDeleteAppId(null);
        setDeleteReason("");
        setDeleteNote("");
      },
      onError: (err: Error) => {
        console.error("Failed to delete application:", err);
      },
    },
  );

  // ── Handlers ─────────────────────────────────────────────────
  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handleSave = () => {
    setShowEditModal(false);
    queryClient.invalidateQueries(["job", id]);
  };

  const handleDelete = async () => {
    if (
      job &&
      window.confirm(
        t("recruiting:jobs.form.confirmDelete", { title: job.title }),
      )
    ) {
      try {
        await recruitingAPI.deleteJob(job.id);
        navigate("/jobs");
      } catch (error) {
        console.error("Error deleting job:", error);
        alert(t("recruiting:jobs.errors.deleteFailed"));
      }
    }
  };

  const handleApplicationSave = () => {
    setShowCreateApplicationModal(false);
    queryClient.invalidateQueries(["job-applications", id]);
  };

  const handleGenerateCV = (application: Application) => {
    setSelectedApplicationForCV(application);
    setShowCVModal(true);
  };

  const handleDeleteApplication = () => {
    if (deleteAppId && deleteReason) {
      deleteApplicationMutation.mutate({
        appId: deleteAppId,
        reason: deleteReason,
        note: deleteNote,
      });
    }
  };

  const handleAssignRecruiter = async () => {
    if (!selectedRecruiterId || !job) return;
    setIsAssigningRecruiter(true);
    try {
      await recruitingAPI.assignRecruiterToJob(
        job.id,
        Number.parseInt(selectedRecruiterId, 10),
      );
      setSelectedRecruiterId("");
      queryClient.invalidateQueries(["job", id]);
    } catch (error) {
      console.error("Error assigning recruiter:", error);
      alert(t("recruiting:jobs.errors.assignFailed"));
    } finally {
      setIsAssigningRecruiter(false);
    }
  };

  const handleRemoveRecruiter = async (recruiterId: number) => {
    if (!job) return;
    setRemovingRecruiterId(recruiterId);
    try {
      await recruitingAPI.removeRecruiterFromJob(job.id, recruiterId);
      queryClient.invalidateQueries(["job", id]);
    } catch (error) {
      console.error("Error removing recruiter assignment:", error);
      alert(t("recruiting:jobs.errors.removeFailed"));
    } finally {
      setRemovingRecruiterId(null);
    }
  };

  // ── Loading / Error states ───────────────────────────────────
  if (jobLoading) {
    return (
      <div className="job-detail">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t("jobs.detail.errors.loading")}</p>
        </div>
      </div>
    );
  }

  if (jobError || !job) {
    return (
      <div className="job-detail">
        <div className="error-state">
          <h3>{t("jobs.errors.loadingJobs")}</h3>
          <p>
            {(jobError as Error)?.message || t("jobs.detail.errors.notFound")}
          </p>
          <button className="btn-primary" onClick={() => navigate("/jobs")}>
            {t("common:actions.back")}
          </button>
        </div>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────
  return (
    <div className="job-detail">
      {/* Header */}
      <div className="job-detail-header">
        <div className="header-left">
          <button className="job-back-link" onClick={() => navigate("/jobs")}>
            &larr; {t("common:actions.goBack")}
          </button>
          <h1>{job.title}</h1>
          <div className="job-meta">
            <span className="meta-item">
              {job.department || t("jobs.detail.labels.noDepartment")}
            </span>
            {job.location && <span className="meta-item">{job.location}</span>}
          </div>
        </div>
        <div className="header-actions">
          {isEditMode ? (
            <>
              <button
                className="btn-primary"
                onClick={handleSaveEdit}
                disabled={!isDirty || isSaving}
              >
                {isSaving
                  ? t("common:loading.saving")
                  : t("common:actions.save")}
              </button>
              <button className="btn-secondary" onClick={handleCancelEdit}>
                {t("common:actions.cancel")}
              </button>
            </>
          ) : (
            <>
              <button className="btn-outline" onClick={handleStartEdit}>
                {t("common:actions.edit")}
              </button>
              <button className="btn-primary" onClick={handleEdit}>
                {t("jobs.detail.buttons.editAll")}
              </button>
              <button className="btn-delete" onClick={handleDelete}>
                {t("common:actions.delete")}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Compact Status Control */}
      <JobStatusControl
        job={job}
        disabled={!canManageAssignments && currentUser.role !== "sales"}
      />

      {/* 2-Column Layout */}
      <div className="job-detail-body">
        {/* Main Content */}
        <div className="job-detail-main">
          {/* Tabs */}
          <div className="job-tabs">
            <button
              className={`tab-button ${activeTab === "overview" ? "active" : ""}`}
              onClick={() => setActiveTab("overview")}
            >
              {t("jobs.detail.tabs.overview")}
            </button>
            <button
              className={`tab-button ${activeTab === "applications" ? "active" : ""}`}
              onClick={() => setActiveTab("applications")}
            >
              {t("jobs.detail.tabs.applications")} ({applications.length})
            </button>
            {(job?.status === "find_candidates_passive" ||
              job?.status === "find_candidates_active") && (
              <button
                className={`tab-button ${activeTab === "sourcing" ? "active" : ""}`}
                onClick={() => setActiveTab("sourcing")}
              >
                {t("jobs.detail.tabs.sourcing")}
              </button>
            )}
            <button
              className={`tab-button ${activeTab === "ai-tools" ? "active" : ""}`}
              onClick={() => setActiveTab("ai-tools")}
            >
              {t("jobs.detail.tabs.aiTools")}
            </button>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {/* ── Overview Tab ── */}
            {activeTab === "overview" && (
              <div className="summary-tab">
                {/* Inline editable fields */}
                {isEditMode && (
                  <div className="info-section">
                    <h3>{t("jobs.detail.sections.editJobDetails")}</h3>
                    {validationErrors.general && (
                      <div
                        style={{
                          color: "#dc2626",
                          marginBottom: 12,
                          fontSize: 13,
                        }}
                      >
                        {validationErrors.general}
                      </div>
                    )}
                    <div className="info-grid">
                      <InlineEditableField
                        label={t("jobs.detail.labels.title")}
                        value={editedJob.title}
                        isEditing={true}
                        onChange={(v) => handleFieldChange("title", v)}
                        error={validationErrors.title}
                        required
                      />
                      <InlineEditableField
                        label={t("jobs.detail.labels.department")}
                        value={editedJob.department}
                        isEditing={true}
                        onChange={(v) => handleFieldChange("department", v)}
                      />
                      <InlineEditableField
                        label={t("jobs.detail.labels.location")}
                        value={editedJob.location}
                        isEditing={true}
                        onChange={(v) => handleFieldChange("location", v)}
                      />
                      <InlineEditableField
                        label={t("jobs.detail.labels.employmentType")}
                        value={editedJob.employment_type}
                        isEditing={true}
                        onChange={(v) =>
                          handleFieldChange("employment_type", v)
                        }
                      />
                      <InlineEditableField
                        label={t("jobs.detail.labels.seniority")}
                        value={editedJob.seniority}
                        isEditing={true}
                        onChange={(v) => handleFieldChange("seniority", v)}
                      />
                      <InlineEditableField
                        label={t("jobs.detail.labels.customerRate")}
                        value={editedJob.customer_rate}
                        type="number"
                        isEditing={true}
                        onChange={(v) => handleFieldChange("customer_rate", v)}
                      />
                      <InlineEditableField
                        label={t("jobs.detail.labels.engineerRate")}
                        value={editedJob.engineer_rate}
                        type="number"
                        isEditing={true}
                        onChange={(v) => handleFieldChange("engineer_rate", v)}
                      />
                    </div>
                    <div style={{ marginTop: 16 }}>
                      <InlineEditableField
                        label={t("jobs.detail.sections.description")}
                        value={editedJob.description}
                        type="textarea"
                        isEditing={true}
                        onChange={(v) => handleFieldChange("description", v)}
                      />
                    </div>
                  </div>
                )}

                {/* Read-only description */}
                {!isEditMode && job.description && (
                  <div className="info-section">
                    <h3>{t("jobs.detail.sections.description")}</h3>
                    <div
                      className={`description-wrapper${descExpanded ? "" : " collapsed"}`}
                    >
                      <div className="description-text">{job.description}</div>
                      <button
                        className="description-toggle"
                        onClick={() => setDescExpanded((prev) => !prev)}
                      >
                        {descExpanded
                          ? t("common:actions.showLess")
                          : t("common:actions.showMore")}
                      </button>
                    </div>
                  </div>
                )}

                {/* Tags */}
                {!isEditMode && safeStringToArray(job.tags).length > 0 && (
                  <div className="info-section">
                    <h3>{t("jobs.detail.sections.tags")}</h3>
                    <div className="tags-list">
                      {safeStringToArray(job.tags).map((tag, index) => (
                        <span key={index} className="tag">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Company info (merged from old company tab) */}
                {company && (
                  <div className="info-section">
                    <h3>{t("jobs.detail.company.title")}</h3>
                    <div className="info-grid">
                      <div className="info-item">
                        <label>
                          {t("jobs.detail.company.labels.companyName")}
                        </label>
                        <span>
                          {company.company_name ||
                            company.name ||
                            t("jobs.detail.labels.notSpecified")}
                        </span>
                      </div>
                      {company.industry && (
                        <div className="info-item">
                          <label>
                            {t("jobs.detail.company.labels.industry")}
                          </label>
                          <span>{company.industry}</span>
                        </div>
                      )}
                      {company.email && (
                        <div className="info-item">
                          <label>{t("jobs.detail.company.labels.email")}</label>
                          <span>
                            <a href={`mailto:${company.email}`}>
                              {company.email}
                            </a>
                          </span>
                        </div>
                      )}
                      {company.phone && (
                        <div className="info-item">
                          <label>{t("jobs.detail.company.labels.phone")}</label>
                          <span>{company.phone}</span>
                        </div>
                      )}
                      {company.website && (
                        <div className="info-item">
                          <label>
                            {t("jobs.detail.company.labels.website")}
                          </label>
                          <span>
                            <a
                              href={company.website}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {company.website}
                            </a>
                          </span>
                        </div>
                      )}
                    </div>
                    {company.description && (
                      <div style={{ marginTop: 12 }}>
                        <div className="description-text">
                          {company.description}
                        </div>
                      </div>
                    )}
                    {companyNotes.length > 0 && (
                      <div style={{ marginTop: 16 }}>
                        <h4 style={{ fontSize: 14, marginBottom: 8 }}>
                          {t("jobs.detail.company.companyComments")}
                        </h4>
                        {companyNotes.map((note) => (
                          <div
                            key={note.id}
                            className="note-card"
                            style={{ marginBottom: 8 }}
                          >
                            <div className="note-header">
                              <span className="note-author">
                                {note.author_name || t("common:misc.unknown")}
                              </span>
                              <span className="note-date">
                                {new Date(note.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="note-content">{note.content}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── Applications Tab ── */}
            {activeTab === "applications" && (
              <div className="applications-tab">
                <div className="applications-header">
                  <h3>{t("jobs.detail.tabs.applications")}</h3>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <ToggleButtonGroup
                      value={appViewMode}
                      exclusive
                      onChange={(_e, val) => val && setAppViewMode(val)}
                      size="small"
                    >
                      <ToggleButton value="rows" aria-label="List view">
                        <List size={16} />
                      </ToggleButton>
                      <ToggleButton value="cards" aria-label="Card view">
                        <LayoutGrid size={16} />
                      </ToggleButton>
                    </ToggleButtonGroup>
                    <button
                      className="btn-primary"
                      onClick={() => setShowCreateApplicationModal(true)}
                    >
                      {t("jobs.detail.buttons.newApplication")}
                    </button>
                  </div>
                </div>

                {applicationsLoading ? (
                  <div className="loading-state">
                    <div className="spinner"></div>
                    <p>{t("common:loading.loading")}</p>
                  </div>
                ) : applications.length === 0 ? (
                  <div className="empty-state">
                    <p>{t("applications.list.noApplications")}</p>
                  </div>
                ) : (
                  <>
                    <div style={{ marginBottom: "16px" }}>
                      <StatusDistributionBar
                        statusCounts={APPLICATION_STATUS_ORDER.map(
                          (status) => ({
                            status,
                            count: applications.filter(
                              (a) => a.status === status,
                            ).length,
                          }),
                        )}
                        total={applications.length}
                        onSegmentClick={(status) => {
                          setAppStatusFilter((prev) =>
                            prev === status ? null : status,
                          );
                        }}
                      />
                    </div>

                    {appStatusFilter && (
                      <div
                        style={{
                          marginBottom: "8px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Chip
                          label={`${t("common:misc.filtered")}: ${APPLICATION_STATUS_CONFIG[appStatusFilter]?.label || appStatusFilter}`}
                          onDelete={() => setAppStatusFilter(null)}
                          size="small"
                          sx={{
                            backgroundColor:
                              APPLICATION_STATUS_CONFIG[appStatusFilter]
                                ?.bgColor || "#f3f4f6",
                            color:
                              APPLICATION_STATUS_CONFIG[appStatusFilter]
                                ?.color || "#374151",
                          }}
                        />
                      </div>
                    )}

                    {appViewMode === "rows" ? (
                      <ApplicationListView
                        applications={applications.filter(
                          (a) =>
                            !appStatusFilter || a.status === appStatusFilter,
                        )}
                        onEmailClick={(candidateId) => {
                          setSelectedCandidateForEmail(candidateId);
                          setShowEmailModal(true);
                        }}
                        onGenerateCVClick={handleGenerateCV}
                      />
                    ) : (
                      <div className="applications-list">
                        {applications
                          .filter(
                            (a) =>
                              !appStatusFilter || a.status === appStatusFilter,
                          )
                          .map((application) => {
                            const statusConfig =
                              APPLICATION_STATUS_CONFIG[application.status];
                            return (
                              <div
                                key={application.id}
                                className="application-card"
                                style={{
                                  cursor: "pointer",
                                  position: "relative",
                                }}
                              >
                                <a
                                  href={`/applications/${application.id}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    navigate(`/applications/${application.id}`);
                                  }}
                                  aria-label={`${application.candidate_first_name || application.first_name} ${application.candidate_last_name || application.last_name}`}
                                  style={{
                                    position: "absolute",
                                    inset: 0,
                                    zIndex: 1,
                                  }}
                                />
                                <div className="application-header">
                                  <h4>
                                    {application.candidate_first_name ||
                                      application.first_name}{" "}
                                    {application.candidate_last_name ||
                                      application.last_name}
                                  </h4>
                                  <Chip
                                    label={
                                      statusConfig?.label || application.status
                                    }
                                    size="small"
                                    sx={{
                                      backgroundColor:
                                        statusConfig?.color || "#6b7280",
                                      color: "white",
                                      fontWeight: 500,
                                      fontSize: "11px",
                                    }}
                                  />
                                </div>
                                <div className="application-details">
                                  <p>
                                    {t("applications.card.labels.email")}{" "}
                                    {application.candidate_email ||
                                      application.email ||
                                      t("applications.card.labels.noEmail")}
                                  </p>
                                  <p>
                                    {t("applications.card.labels.mainSkill")}{" "}
                                    {application.main_skill ||
                                      t("jobs.detail.labels.notSpecified")}
                                  </p>
                                  {application.ai_match_score != null && (
                                    <p>
                                      {t("applications.card.labels.aiMatch")}{" "}
                                      {application.ai_match_score}%
                                    </p>
                                  )}
                                </div>
                                <div style={{ margin: "8px 0" }}>
                                  <ApplicationStatusTimeline
                                    currentStatus={application.status}
                                    statusChangedAt={
                                      application.status_changed_at
                                    }
                                    compact
                                  />
                                </div>
                                <div
                                  className="application-actions"
                                  style={{
                                    position: "relative",
                                    zIndex: 2,
                                  }}
                                >
                                  <QuickStatusChange
                                    applicationId={application.id}
                                    currentStatus={application.status}
                                    compact
                                  />
                                  <button
                                    className="btn-outline"
                                    onClick={() =>
                                      navigate(
                                        `/candidates/${application.candidate_id}`,
                                      )
                                    }
                                  >
                                    {t(
                                      "applications.card.buttons.viewCandidate",
                                    )}
                                  </button>
                                  <button
                                    className="btn-primary"
                                    onClick={() => {
                                      setSelectedCandidateForEmail(
                                        application.candidate_id,
                                      );
                                      setShowEmailModal(true);
                                    }}
                                  >
                                    {t("applications.card.buttons.email")}
                                  </button>
                                  <button
                                    className="btn-secondary"
                                    onClick={() =>
                                      handleGenerateCV(application)
                                    }
                                  >
                                    {t("applications.card.buttons.generateCV")}
                                  </button>
                                  <button
                                    className="btn-outline"
                                    style={{
                                      color: "#dc2626",
                                      borderColor: "#dc2626",
                                    }}
                                    onClick={() =>
                                      setDeleteAppId(application.id)
                                    }
                                  >
                                    {t(
                                      "applications.card.buttons.delete",
                                      "Delete",
                                    )}
                                  </button>
                                </div>
                                {deleteAppId === application.id && (
                                  <div
                                    role="presentation"
                                    style={{
                                      position: "relative",
                                      zIndex: 3,
                                      marginTop: "8px",
                                      padding: "12px",
                                      border: "1px solid #fecaca",
                                      borderRadius: "8px",
                                      backgroundColor: "#fef2f2",
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <p
                                      style={{
                                        margin: "0 0 8px",
                                        fontWeight: 600,
                                        color: "#991b1b",
                                        fontSize: "13px",
                                      }}
                                    >
                                      {t(
                                        "applications.delete.title",
                                        "Delete this application?",
                                      )}
                                    </p>
                                    <select
                                      value={deleteReason}
                                      onChange={(e) =>
                                        setDeleteReason(e.target.value)
                                      }
                                      style={{
                                        width: "100%",
                                        padding: "6px 8px",
                                        marginBottom: "8px",
                                        borderRadius: "4px",
                                        border: "1px solid #d1d5db",
                                        fontSize: "13px",
                                      }}
                                    >
                                      <option value="">
                                        {t(
                                          "applications.delete.selectReason",
                                          "Select a reason...",
                                        )}
                                      </option>
                                      <option value="duplicate">
                                        {t(
                                          "applications.delete.reasons.duplicate",
                                          "Duplicate Application",
                                        )}
                                      </option>
                                      <option value="wrong_job">
                                        {t(
                                          "applications.delete.reasons.wrongJob",
                                          "Wrong Job",
                                        )}
                                      </option>
                                      <option value="created_by_mistake">
                                        {t(
                                          "applications.delete.reasons.mistake",
                                          "Created by Mistake",
                                        )}
                                      </option>
                                      <option value="test_data">
                                        {t(
                                          "applications.delete.reasons.testData",
                                          "Test Data",
                                        )}
                                      </option>
                                      <option value="other">
                                        {t(
                                          "applications.delete.reasons.other",
                                          "Other (specify in notes)",
                                        )}
                                      </option>
                                    </select>
                                    <textarea
                                      value={deleteNote}
                                      onChange={(e) =>
                                        setDeleteNote(e.target.value)
                                      }
                                      placeholder={t(
                                        "applications.delete.notePlaceholder",
                                        "Optional note...",
                                      )}
                                      rows={2}
                                      style={{
                                        width: "100%",
                                        padding: "6px 8px",
                                        marginBottom: "8px",
                                        borderRadius: "4px",
                                        border: "1px solid #d1d5db",
                                        fontSize: "13px",
                                        resize: "vertical",
                                      }}
                                    />
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: "8px",
                                        justifyContent: "flex-end",
                                      }}
                                    >
                                      <button
                                        className="btn-outline"
                                        onClick={() => {
                                          setDeleteAppId(null);
                                          setDeleteReason("");
                                          setDeleteNote("");
                                        }}
                                      >
                                        {t("common:cancel", "Cancel")}
                                      </button>
                                      <button
                                        className="btn-primary"
                                        style={{
                                          backgroundColor: "#dc2626",
                                          borderColor: "#dc2626",
                                        }}
                                        disabled={
                                          !deleteReason ||
                                          deleteApplicationMutation.isLoading
                                        }
                                        onClick={handleDeleteApplication}
                                      >
                                        {deleteApplicationMutation.isLoading
                                          ? t(
                                              "applications.delete.deleting",
                                              "Deleting...",
                                            )
                                          : t(
                                              "applications.delete.confirm",
                                              "Delete",
                                            )}
                                      </button>
                                    </div>
                                    {deleteApplicationMutation.isError && (
                                      <p
                                        style={{
                                          color: "#dc2626",
                                          fontSize: "12px",
                                          marginTop: "4px",
                                        }}
                                      >
                                        {t(
                                          "applications.delete.error",
                                          "Failed to delete application. Please try again.",
                                        )}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* ── Sourcing Tab ── */}
            {activeTab === "sourcing" && job && (
              <SourcingTab jobId={job.id} jobTitle={job.title} />
            )}

            {/* ── AI Tools Tab ── */}
            {activeTab === "ai-tools" && (
              <div className="ai-tools-tab">
                <div className="ai-tool-section">
                  <JobDescriptionExtension
                    jobId={Number.parseInt(id!, 10)}
                    currentDescription={job.description}
                    onAccept={(extendedDescription: string) => {
                      recruitingAPI
                        .updateJob(Number.parseInt(id!, 10), {
                          description: extendedDescription,
                        })
                        .then(() => {
                          queryClient.invalidateQueries(["job", id]);
                          alert(t("jobs.detail.messages.descriptionUpdated"));
                        })
                        .catch((error) => {
                          console.error("Error updating description:", error);
                          alert(t("jobs.detail.messages.descriptionFailed"));
                        });
                    }}
                  />
                </div>
                <div className="ai-tool-section">
                  <JobCandidateFinder
                    jobId={Number.parseInt(id!, 10)}
                    onCreateApplication={() =>
                      setShowCreateApplicationModal(true)
                    }
                  />
                </div>
                <div className="ai-tool-section">
                  <PublicProfileSearch
                    jobId={Number.parseInt(id!, 10)}
                    onCreateApplication={() =>
                      setShowCreateApplicationModal(true)
                    }
                  />
                </div>
                <div className="ai-tool-section">
                  <LinkedInPostGenerator jobId={Number.parseInt(id!, 10)} />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <JobDetailSidebar
          job={job}
          applications={applications}
          activityItems={activitySummaryData?.data?.recentActivities || []}
          activityLoading={activityLoading}
          recruiters={recruiters}
          selectedRecruiterId={selectedRecruiterId}
          onRecruiterSelect={setSelectedRecruiterId}
          onAssignRecruiter={handleAssignRecruiter}
          onRemoveRecruiter={handleRemoveRecruiter}
          isAssigningRecruiter={isAssigningRecruiter}
          removingRecruiterId={removingRecruiterId}
          canManageAssignments={!!canManageAssignments}
          jobId={Number.parseInt(id!, 10)}
        />
      </div>

      {/* ── Modals ── */}
      {showEditModal && (
        <div className="modal-overlay">
          <div
            className="modal-backdrop"
            onClick={() => setShowEditModal(false)}
            aria-hidden="true"
          />
          <div className="modal job-modal-wide">
            <JobForm
              job={job}
              onSave={handleSave}
              onCancel={() => setShowEditModal(false)}
              onDelete={handleDelete}
            />
          </div>
        </div>
      )}

      {showCreateApplicationModal && (
        <div className="modal-overlay">
          <div
            className="modal-backdrop"
            onClick={() => setShowCreateApplicationModal(false)}
            aria-hidden="true"
          />
          <div className="modal">
            <ApplicationForm
              jobId={Number.parseInt(id!, 10)}
              onSave={handleApplicationSave}
              onCancel={() => setShowCreateApplicationModal(false)}
            />
          </div>
        </div>
      )}

      {showEmailModal && selectedCandidateForEmail != null && (
        <div className="modal-overlay">
          <div className="modal large-modal">
            <EmailComposer
              candidateId={selectedCandidateForEmail}
              jobId={Number.parseInt(id!, 10)}
              mode="recruiter"
              onClose={() => {
                setShowEmailModal(false);
                setSelectedCandidateForEmail(null);
              }}
              onSent={() => {
                queryClient.invalidateQueries(["job-applications", id]);
                setShowEmailModal(false);
                setSelectedCandidateForEmail(null);
              }}
            />
          </div>
        </div>
      )}

      {showCVModal && selectedApplicationForCV && (
        <JobSpecificCVGeneration
          applicationId={selectedApplicationForCV.id}
          candidateName={`${selectedApplicationForCV.candidate_first_name || selectedApplicationForCV.first_name || ""} ${selectedApplicationForCV.candidate_last_name || selectedApplicationForCV.last_name || ""}`.trim()}
          jobTitle={job?.title || "Job"}
          onClose={() => {
            setShowCVModal(false);
            setSelectedApplicationForCV(null);
          }}
        />
      )}
    </div>
  );
};

export default JobDetail;
